import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import * as Sentry from '@sentry/react';
import { useAuth } from '../lib/auth/auth-context';
import { supabase as defaultClient } from '../lib/supabase';
import type { LynrxSupabaseClient } from '@lynrx/shared';
import { Alert } from '../components/ui/Alert';
import { DataTable, type Column } from '../components/ui/DataTable';
import { StatusBadge } from '../components/ui/StatusBadge';
import { useSetPageHeader } from '../components/layout/PageHeaderContext';

// Mirrors public.driver_status (lynrx-prod, May 7 2026 migration).
export const DRIVER_STATUS_VALUES = [
  'active',
  'on_leave',
  'suspended',
  'terminated',
] as const;

export type DriverStatusValue = (typeof DRIVER_STATUS_VALUES)[number];
export type DriverStatusFilter = 'all' | DriverStatusValue;

const STATUS_FILTER_VALUES: readonly DriverStatusFilter[] = [
  'all',
  ...DRIVER_STATUS_VALUES,
];

function isDriverStatusFilter(value: unknown): value is DriverStatusFilter {
  return (
    typeof value === 'string' &&
    (STATUS_FILTER_VALUES as readonly string[]).includes(value)
  );
}

function statusFilterLabel(value: DriverStatusFilter): string {
  if (value === 'all') return 'All';
  const words = value.split('_');
  const first = words[0];
  if (!first) return value;
  const head = first.charAt(0).toUpperCase() + first.slice(1).toLowerCase();
  return [head, ...words.slice(1).map((w) => w.toLowerCase())].join(' ');
}

type DriversSearch = {
  status: DriverStatusFilter;
};

export const Route = createFileRoute('/_authenticated/drivers/')({
  component: DriversListPage,
  validateSearch: (search: Record<string, unknown>): DriversSearch => {
    const raw = search['status'];
    if (isDriverStatusFilter(raw)) return { status: raw };
    return { status: 'active' };
  },
});

// Shape returned by the joined select below. drivers join is 1:1
// (drivers.id is also the FK to users.id), so PostgREST returns it as
// a single object or null. pharmacy_access(count) returns an array of
// one row carrying the aggregate count for the user.
type DriverListRow = {
  id: string;
  full_name: string | null;
  email: string;
  employee_id: string | null;
  drivers: {
    status: string;
    phone: string | null;
    hire_date: string | null;
  } | null;
  pharmacy_access: { count: number }[];
};

// public.users.created_at — fallback when hire_date is null. Both are
// ISO strings; hire_date is a date column (YYYY-MM-DD), created_at is
// a timestamptz.
function parseLooseDate(iso: string): Date | null {
  // Date-only strings parse as UTC midnight in JS. To render relative
  // labels against a local "today," interpret the YYYY-MM-DD form as
  // local time.
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.exec(iso);
  if (dateOnly) {
    const parts = iso.split('-');
    const y = Number(parts[0]);
    const m = Number(parts[1]);
    const d = Number(parts[2]);
    if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) return null;
    return new Date(y, m - 1, d);
  }
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

// Relative date formatter tuned for hire dates: days/weeks/months
// resolution, falling back to the absolute date past ~1 year. Day-
// granularity (not minutes/hours) because hire_date is a date column.
function formatRelativeHireDate(iso: string, now: Date = new Date()): string {
  const then = parseLooseDate(iso);
  if (!then) return iso;
  const today = startOfLocalDay(now);
  const thenDay = startOfLocalDay(then);
  const diffMs = today.getTime() - thenDay.getTime();
  const diffDays = Math.round(diffMs / (24 * 60 * 60 * 1000));
  if (diffDays <= 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) {
    const w = Math.round(diffDays / 7);
    return `${w} week${w === 1 ? '' : 's'} ago`;
  }
  if (diffDays < 365) {
    const m = Math.round(diffDays / 30);
    return `${m} month${m === 1 ? '' : 's'} ago`;
  }
  return then.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function DriversListPage(): JSX.Element {
  const { status } = Route.useSearch();
  const navigate = useNavigate();
  const setStatus = useCallback(
    (next: DriverStatusFilter) => {
      void navigate({ to: '/drivers', search: { status: next } });
    },
    [navigate],
  );
  return <DriversListView status={status} onStatusChange={setStatus} />;
}

type ViewProps = {
  status: DriverStatusFilter;
  onStatusChange: (next: DriverStatusFilter) => void;
  // Test seam — defaults to the singleton supabase client.
  client?: LynrxSupabaseClient;
};

export function DriversListView({
  status,
  onStatusChange,
  client = defaultClient,
}: ViewProps): JSX.Element {
  const { session, isReady } = useAuth();
  useSetPageHeader({
    title: 'Drivers',
    subtitle: 'All drivers in your tenant.',
  });
  const [rows, setRows] = useState<DriverListRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isReady || !session) return;
    let cancelled = false;
    void (async () => {
      setError(null);
      // !inner on the drivers join: every user_type='driver' must have
      // a drivers row (create_driver RPC is transactional), and the
      // inner-join lets PostgREST filter on drivers.status server-side.
      let query = client
        .from('users')
        .select(
          'id, full_name, email, employee_id, drivers:drivers!drivers_id_fkey!inner(status, phone, hire_date), pharmacy_access:pharmacy_access!pharmacy_access_user_id_fkey(count)',
        )
        .eq('user_type', 'driver')
        .is('deleted_at', null)
        .order('full_name', { ascending: true });
      if (status !== 'all') {
        query = query.eq('drivers.status', status);
      }
      const { data, error: err } = await query;
      if (cancelled) return;
      if (err) {
        Sentry.captureException(err, { tags: { area: 'drivers.list.fetch' } });
        setError('Could not load drivers.');
        setRows([]);
        return;
      }
      setRows((data ?? []) as unknown as DriverListRow[]);
    })();
    return () => {
      cancelled = true;
    };
  }, [client, isReady, session, status]);

  const columns: Column<DriverListRow>[] = useMemo(
    () => [
      {
        key: 'employee_id',
        header: 'Employee ID',
        render: (r) => r.employee_id ?? '—',
      },
      {
        key: 'full_name',
        header: 'Name',
        render: (r) => (
          <Link
            to="/drivers/$driverId"
            params={{ driverId: r.id }}
            className="text-primary hover:underline"
          >
            {r.full_name ?? '—'}
          </Link>
        ),
      },
      { key: 'email', header: 'Email', render: (r) => r.email },
      {
        key: 'phone',
        header: 'Phone',
        render: (r) => r.drivers?.phone ?? '—',
      },
      {
        key: 'status',
        header: 'Status',
        render: (r) =>
          r.drivers ? (
            <StatusBadge entity="route" status={r.drivers.status} />
          ) : (
            '—'
          ),
      },
      {
        key: 'pharmacies',
        header: 'Pharmacies',
        render: (r) => String(r.pharmacy_access[0]?.count ?? 0),
      },
      {
        key: 'hired',
        header: 'Hired',
        render: (r) =>
          r.drivers?.hire_date
            ? formatRelativeHireDate(r.drivers.hire_date)
            : '—',
      },
    ],
    [],
  );

  return (
    <>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <label className="flex items-center gap-3 text-sm">
          <span className="font-medium text-ink">Status</span>
          <select
            value={status}
            onChange={(e) => {
              const next = e.target.value;
              if (isDriverStatusFilter(next)) onStatusChange(next);
            }}
            className="rounded-md border border-border bg-bg px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-primary"
            aria-label="Filter drivers by status"
          >
            {STATUS_FILTER_VALUES.map((s) => (
              <option key={s} value={s}>
                {statusFilterLabel(s)}
              </option>
            ))}
          </select>
        </label>
        <Link
          to="/drivers/new"
          className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-dark"
        >
          Add driver
        </Link>
      </div>

      {error ? (
        <div className="mb-6">
          <Alert tone="error">{error}</Alert>
        </div>
      ) : null}

      <DataTable
        columns={columns}
        rows={rows ?? []}
        rowKey={(r) => r.id}
        emptyMessage={
          rows === null ? (
            'Loading drivers…'
          ) : status === 'all' ? (
            <span>
              No drivers yet.{' '}
              <Link to="/drivers/new" className="text-primary hover:underline">
                Add your first driver →
              </Link>
            </span>
          ) : (
            `No drivers with status "${statusFilterLabel(status)}".`
          )
        }
      />
    </>
  );
}
